import { orgAPI } from "@/api/api";

const IMPORT_POLL_INTERVAL_MS = 750;
/**
 * Longer than the publish poll's 90s: an import copies the artifact's file
 * objects serially before hydrating state, so big canvases take a while.
 */
const IMPORT_POLL_TIMEOUT_MS = 180_000;

export type ImportedCanvasTerminalStatus = "ready" | "failed" | "timeout";

/**
 * Poll GET canvas until `import_status` leaves `"importing"` (wait-then-redirect
 * UX: the viewer holds "Adding…" until the copy is fully hydrated, then
 * navigates). Resolves `ready` when the status clears, `failed` on the failure
 * marker, `timeout` when the deadline passes (the import may still finish —
 * the canvas is already in the workspace list). Transient GET errors keep
 * polling; an abort resolves `timeout` immediately.
 */
export async function pollImportedCanvasTerminal(
  canvasId: string,
  signal?: AbortSignal,
): Promise<ImportedCanvasTerminalStatus> {
  const deadline = Date.now() + IMPORT_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    if (signal?.aborted) return "timeout";
    try {
      const canvas = await orgAPI.canvas.getById(canvasId);
      if (signal?.aborted) return "timeout";
      const status = canvas.import_status ?? "";
      if (status === "failed") return "failed";
      if (status !== "importing") return "ready";
    } catch {
      // Transient GET failures (or the shell not being readable yet) keep
      // polling until the deadline.
    }
    if (signal?.aborted) return "timeout";
    await new Promise((resolve) =>
      setTimeout(resolve, IMPORT_POLL_INTERVAL_MS),
    );
  }

  return "timeout";
}
